import React, { useState, useEffect, useMemo } from "react";
import { Box, Grid, Container, Card, Skeleton, Stack } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import cinstance from "../../Service/randomcinstance";
import usercinstance from "../../Service/usercinstance";
import web3 from "../../Service/web3";

import { colors } from "../../../theme";
import { Path } from "../../../static/Constants";
import { AdminLayout } from "../../Components/dashboard";
import { CommonInput, NoData } from "../../Components/ui";
import CreatorCard from "../../Components/CreatorCard";

const safeFromWei = (val) => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, "ether");
  } catch (e) {
    return "0";
  }
};

/**
 * CreatorCardSkeleton matching exact card dimensions & design tokens
 */
const CreatorCardSkeleton = () => (
  <Card
    sx={{
      position: "relative",
      overflow: "hidden",
      borderRadius: "24px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
      border: "1px solid rgba(0,0,0,0.03)",
      bgcolor: "#ffffff",
      py: 1.5,
      px: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* Top Row: Avatar & Status */}
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
      <Skeleton variant="circular" width={40} height={40} animation="wave" />
      <Skeleton variant="rounded" width={76} height={24} animation="wave" sx={{ borderRadius: "999px" }} />
    </Box>

    {/* Header Info */}
    <Box sx={{ mb: 1 }}>
      <Skeleton variant="text" width={50} height={14} animation="wave" sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="60%" height={24} animation="wave" />
    </Box>

    {/* Metrics Row */}
    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
      <Box sx={{ flex: 1, bgcolor: "#f2f6ff", borderRadius: "12px", p: 1.25 }}>
        <Skeleton variant="text" width={60} height={14} animation="wave" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="70%" height={20} animation="wave" />
      </Box>
      <Box sx={{ flex: 1, bgcolor: "#f8f4ff", borderRadius: "12px", p: 1.25 }}>
        <Skeleton variant="text" width={60} height={14} animation="wave" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="70%" height={20} animation="wave" />
      </Box>
    </Stack>

    {/* Address Section */}
    <Box sx={{ mb: "auto" }}>
      <Skeleton variant="text" width={50} height={14} animation="wave" sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="90%" height={18} animation="wave" />
    </Box>

    {/* Bottom Button Skeleton */}
    <Box sx={{ mt: 1.5 }}>
      <Skeleton variant="rounded" width="100%" height={38} animation="wave" sx={{ borderRadius: "10px" }} />
    </Box>
  </Card>
);

function OrganisationList() {
  const [orglist, setOrglist] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [adminAccount, setAdminAccount] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      var orglistArray = [];
      setLoading(true);
      if (!window.ethereum && !web3) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      window.web3 = web3;
      try {
        let accounts = [];
        if (window.ethereum?.request) {
          accounts = await window.ethereum.request({ method: "eth_accounts" });
        }
        if (!accounts || accounts.length === 0) {
          accounts = await web3.eth.getAccounts();
        }
        const userAccount = accounts && accounts.length > 0 ? accounts[0] : (window.ethereum?.selectedAddress || "");
        setAdminAccount(userAccount);

        let contractAdmin = "";
        try {
          if (cinstance?.methods?.admin) {
            contractAdmin = await cinstance.methods.admin().call();
          }
        } catch (err) {
          console.warn("Could not fetch contract admin:", err);
        }

        const authorized = Boolean(
          contractAdmin &&
          userAccount &&
          contractAdmin.toLowerCase() === userAccount.toLowerCase()
        );
        setIsAdmin(authorized);
        setAuthChecked(true);

        if (!authorized) {
          setLoading(false);
          return;
        }

        if (usercinstance?.methods?.ownerId) {
          const res = await usercinstance.methods.ownerId().call();
          const count = Number(res);
          for (var i = count - 1; i > 0; i--) {
            try {
              const responseAddress = await usercinstance.methods.organisationbyid(i).call();
              const response = await usercinstance.methods.organisationbyaddr(responseAddress).call();
              if (response && response.userAddress && response.userAddress !== "0x0000000000000000000000000000000000000000") {
                orglistArray.push(response);
              }
            } catch (err) {
              console.log("pepep", err);
            }
          }
          setOrglist(orglistArray);
        }
      } catch (err) {
        console.log("res", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const approve = async (id) => {
    if (window.ethereum) {
      window.web3 = web3;
      let accounts = [];
      if (window.ethereum?.request) {
        accounts = await window.ethereum.request({ method: "eth_accounts" });
      }
      if (!accounts || accounts.length === 0) {
        accounts = await web3.eth.getAccounts();
      }
      const userAccount = accounts && accounts.length > 0 ? accounts[0] : (window.ethereum?.selectedAddress || "");

      try {
        let contractAdmin = "";
        if (cinstance?.methods?.admin) {
          contractAdmin = await cinstance.methods.admin().call();
        }
        const authorized = Boolean(
          contractAdmin &&
          userAccount &&
          contractAdmin.toLowerCase() === userAccount.toLowerCase()
        );
        if (authorized) {
          await cinstance.methods.approveOraganisation(id).send({ from: userAccount });
          window.location.reload();
        } else {
          alert("Not admin");
        }
      } catch (e) {
        console.error("Approve error", e);
      }
    }
  };

  const filteredOrgs = useMemo(() => {
    if (!searchQuery) return orglist;
    const q = searchQuery.toLowerCase().trim();
    return orglist.filter((item) => {
      const nameMatch = item?.name && item.name.toLowerCase().includes(q);
      const idMatch = item?.id && String(item.id).toLowerCase().includes(q);
      const addrMatch = item?.userAddress && item.userAddress.toLowerCase().includes(q);
      return nameMatch || idMatch || addrMatch;
    });
  }, [orglist, searchQuery]);

  return (
    <AdminLayout
      title="Creators List"
      subtitle="Manage and view all registered creators on the AutoBET platform."
      breadcrumbs={[
        { label: "Dashboard", href: Path.admin },
        { label: "Creators List" },
      ]}
      headerActions={
        <CommonInput
          placeholder="Search by name, ID or wallet address..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          startAdornment={
            <SearchIcon sx={{ fontSize: 18, color: colors.neutral.lightGrey }} />
          }
          width={{ xs: "100%", sm: "360px" }}
        />
      }
      creatorAddress={adminAccount}
      authChecked={authChecked}
      isAuthorized={isAdmin}
      accessDeniedProps={{
        message: "You must be an administrator to access the Creators List console.",
      }}
    >
      <Box className="content bookieslist">
        <Grid container spacing={2}>
          {/* Skeleton Loading Grid */}
          {loading &&
            [1, 2, 3, 4, 5, 6, 7, 8].map((idx) => (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={`skel-${idx}`}>
                <CreatorCardSkeleton />
              </Grid>
            ))}

          {/* Empty State with NoData Component */}
          {!loading && filteredOrgs.length === 0 && (
            <Grid item xs={12}>
              <NoData
                title="No Data !"
                subtitle={
                  searchQuery
                    ? `No creators found matching "${searchQuery}".`
                    : "No creators have been registered on the AutoBET platform yet."
                }
                actionText={searchQuery ? "Clear Search" : undefined}
                onAction={searchQuery ? () => setSearchQuery("") : undefined}
              />
            </Grid>
          )}

          {/* Loaded Creator Cards */}
          {!loading &&
            filteredOrgs.map((item, index) => (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={index}>
                <CreatorCard
                  id={item.id}
                  name={item.name}
                  maxPrize={safeFromWei(item.maxPrize)}
                  minPrize={safeFromWei(item.minPrize)}
                  userAddress={item.userAddress}
                  active={item.active}
                  onApprove={() => approve(item.id)}
                />
              </Grid>
            ))}
        </Grid>
      </Box>
    </AdminLayout>
  );
}

export default OrganisationList;
